// src/pages/AchatQuota.js - Version avec Nita & Amana
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import PaiementNitaAmanaModal from "../components/PaiementNitaAmanaModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Compte à rebours jusqu'à l'expiration (24h) d'un code d'achat NITA/Amana
// (même composant que sur AbonnementsDetails.js / AbonnementsCreate.js).
const CompteARebours = ({ createdAt }) => {
  const [texte, setTexte] = useState('');

  useEffect(() => {
    const calculer = () => {
      const limite = new Date(createdAt.replace(' ', 'T')).getTime() + 24 * 60 * 60 * 1000;
      const restantMs = limite - Date.now();
      if (restantMs <= 0) {
        setTexte('Référence expirée');
        return;
      }
      const heures = Math.floor(restantMs / (1000 * 60 * 60));
      const minutes = Math.floor((restantMs % (1000 * 60 * 60)) / (1000 * 60));
      setTexte(`Temps restant pour payer : ${heures}h ${minutes}min`);
    };
    calculer();
    const interval = setInterval(calculer, 60000);
    return () => clearInterval(interval);
  }, [createdAt]);

  return <span>{texte}</span>;
};

const AchatQuota = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const typeParam = queryParams.get('type') || null;

  const [loading, setLoading] = useState(false);
  const [loadingSocietes, setLoadingSocietes] = useState(true);
  const [loadingQuotas, setLoadingQuotas] = useState(true);
  const [serverError, setServerError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [paiementEnCours, setPaiementEnCours] = useState(null); // { id_paiement, montant }
  const [paiementEnAttente, setPaiementEnAttente] = useState(null);
  const [annulationEnCours, setAnnulationEnCours] = useState(false);
  const [societeEnEssai, setSocieteEnEssai] = useState(false);
  const [checkingEssai, setCheckingEssai] = useState(true);
  
  // Données
  const [societes, setSocietes] = useState([]);
  const [quotasActuels, setQuotasActuels] = useState(null);
  const [selectedSociete, setSelectedSociete] = useState(null);
  
  // Formulaire multi-achats avec checkbox
  const [achats, setAchats] = useState([
    { type: 'client', quantite: 10, selected: typeParam === 'client' || typeParam === null },
    { type: 'site', quantite: 10, selected: typeParam === 'site' },
    { type: 'tag_qr', quantite: 10, selected: typeParam === 'tag_qr' },
    { type: 'tag_nfc', quantite: 10, selected: typeParam === 'tag_nfc' }
  ]);

  // États pour le paiement
  const [modePaiement, setModePaiement] = useState("nita");
  const [numeroRecu, setNumeroRecu] = useState("");

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',
    secondary: '#1E88C8',
    accent: '#4FC3F7',
    darkText: '#2E2E2E',
    lightBg: '#F5F7FA',
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3',
    successLight: '#e8f5e9',
    dangerLight: '#ffebee',
    warningLight: '#fff3e0',
    infoLight: '#e3f2fd'
  };

  // Prix unitaires configurables par l'admin (Paramètres > Tarifs des quotas) —
  // valeurs par défaut affichées tant que quota_prix/list.php n'a pas répondu.
  const [prixQuotas, setPrixQuotas] = useState({ client: 5000, site: 10000, tag_qr: 2000, tag_nfc: 3000 });

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    axios.get(`${API_BASE_URL}/quota_prix/list.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (res.data?.success && res.data.prix) {
          setPrixQuotas(res.data.prix);
        }
      })
      .catch(() => {});
  }, []);

  // Passerelles NITA/Amana actives (Paramètres > Passerelles de paiement) —
  // une passerelle désactivée ne doit pas apparaître comme option ici, même
  // logique que dans PaiementNitaAmanaModal.js. null = pas encore chargé.
  const [moyensActifs, setMoyensActifs] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    axios.get(`${API_BASE_URL}/paiements/gateway_public_list.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const actifs = (res.data?.passerelles || []).map(p => p.code);
        setMoyensActifs(actifs);
      })
      .catch(() => setMoyensActifs(['nita', 'amana']));
  }, []);

  // Si le moyen sélectionné par défaut ("nita") n'est plus actif une fois la
  // liste chargée, basculer automatiquement sur le premier moyen disponible.
  useEffect(() => {
    if (moyensActifs === null || currentProfile === "1") return;
    if (moyensActifs.length > 0 && !moyensActifs.includes(modePaiement)) {
      setModePaiement(moyensActifs[0]);
    }
  }, [moyensActifs, currentProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Types de quotas
  const TYPES_QUOTA = [
    { value: 'client', label: 'Clients', icon: 'bi-people', color: '#2196f3', prix: prixQuotas.client },
    { value: 'site', label: 'Sites', icon: 'bi-building', color: '#4caf50', prix: prixQuotas.site },
    { value: 'tag_qr', label: 'Tags QR', icon: 'bi-qr-code', color: '#ff9800', prix: prixQuotas.tag_qr },
    { value: 'tag_nfc', label: 'Tags NFC', icon: 'bi-wifi', color: '#9c27b0', prix: prixQuotas.tag_nfc }
  ];

  // Modes de paiement selon le profil
  const getModesPaiement = () => {
    if (currentProfile === "1") {
      // Admin : espèces uniquement
      return [{ value: 'especes', label: 'Espèces' }];
    } else {
      // Gestionnaire : Nita et Amana, filtrés par les passerelles actives
      const tousLesMoyens = [
        { value: 'nita', label: 'Nita' },
        { value: 'amana', label: 'Amana' }
      ];
      if (moyensActifs === null) return [];
      return tousLesMoyens.filter(m => moyensActifs.includes(m.value));
    }
  };

  // Récupérer les infos utilisateur depuis le localStorage
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        const profile = String(user.profile || "").trim();
        setCurrentProfile(profile);
        
        // Définir le mode de paiement par défaut selon le profil
        if (profile === "1") {
          setModePaiement("especes");
        } else {
          setModePaiement("nita");
        }
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    }
  }, []);

  // Charger les sociétés selon le profil
  useEffect(() => {
    if (currentProfile) {
      fetchSocietes();
    }
  }, [currentProfile, currentUser]); // Ajout de currentUser comme dépendance

  // Charger les quotas selon la société sélectionnée
  useEffect(() => {
    if (selectedSociete?.id_societe) {
      loadQuotasSociete(selectedSociete.id_societe);
      checkEssai(selectedSociete.id_societe);
    }
  }, [selectedSociete]);

  // L'achat de quotas n'est pas disponible pendant une période d'essai (quota
  // fixe, non extensible — voir achat_quota/create.php::ESSAI_ACHAT_INTERDIT).
  // Vérifié ici en amont pour ne même pas proposer le formulaire, plutôt que
  // de laisser l'utilisateur remplir l'achat puis se le faire refuser à la
  // soumission.
  const checkEssai = async (id_societe) => {
    setCheckingEssai(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${API_BASE_URL}/abonnements/verifier_statut.php?societe_id=${id_societe}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSocieteEnEssai(response.data?.details?.type_offre === 'essai');
    } catch (err) {
      console.error("Erreur vérification essai:", err);
    } finally {
      setCheckingEssai(false);
    }
  };

  // Si gestionnaire, pré-sélectionner sa société
  useEffect(() => {
    if (currentProfile === "3" && societes.length > 0 && !selectedSociete) {
      setSelectedSociete(societes[0]);
    }
  }, [societes, currentProfile]);

  // Charger les sociétés - Version corrigée SANS appel à users/me.php
  const fetchSocietes = async () => {
    setLoadingSocietes(true);
    setServerError("");
    // loadQuotasSociete()/checkEssai() ne sont déclenchés que par le useEffect
    // sur selectedSociete, lui-même dépendant de societes.length > 0 : si cette
    // fonction échoue à résoudre une société, ces deux appels n'ont jamais
    // lieu, et leurs spinners ("Vérification de l'abonnement...", "Chargement
        // des quotas...") tournaient donc indéfiniment. On les arrête explicitement
    // dans chaque branche d'échec.
    let societeResolue = false;

    try {
      const token = localStorage.getItem("access_token");

      if (currentProfile === "1") {
        // Admin : voir toutes les sociétés
        const response = await axios.get(`${API_BASE_URL}/societes/list.php`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data?.success) {
          const societesData = response.data.societes.map(s => ({
            id_societe: s.id_societe,
            nom_societe: s.nom_societe
          }));
          setSocietes(societesData);
          societeResolue = societesData.length > 0;
        } else {
          setServerError("Erreur lors du chargement des sociétés");
        }
      } else if (currentProfile === "3") {
        // Gestionnaire : sa propre société - on utilise currentUser du localStorage
        if (currentUser?.societe_id) {
          const response = await axios.get(
            `${API_BASE_URL}/societes/get.php?id=${currentUser.societe_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data?.success) {
            const s = response.data.societe;
            setSocietes([{
              id_societe: s.id_societe,
              nom_societe: s.nom_societe
            }]);
            societeResolue = true;
          } else {
            setServerError("Erreur lors du chargement de votre société");
          }
        } else {
          setServerError("ID société non trouvé pour votre compte");
        }
      }
    } catch (err) {
      console.error("Erreur chargement sociétés:", err);
      setServerError("Erreur lors du chargement des sociétés");
    } finally {
      setLoadingSocietes(false);
      if (!societeResolue) {
        setLoadingQuotas(false);
        setCheckingEssai(false);
      }
    }
  };

  // Charger les quotas d'une société
  const loadQuotasSociete = async (id_societe) => {
    if (!id_societe) return;
    
    setLoadingQuotas(true);
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${API_BASE_URL}/achat_quota/get_restant.php?id_societe=${id_societe}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.success) {
        setQuotasActuels(response.data.quotas);
        setPaiementEnAttente(response.data.paiement_en_attente || null);
      }
    } catch (err) {
      console.error("Erreur chargement quotas société:", err);
    } finally {
      setLoadingQuotas(false);
    }
  };

  // Reprend le paiement en attente (rouvre la modal avec le même id_paiement)
  const handleFinaliserPaiementEnAttente = () => {
    if (!paiementEnAttente) return;
    setPaiementEnCours({ id_paiement: paiementEnAttente.id_paiement, montant: paiementEnAttente.montant });
    setShowPaiementModal(true);
  };

  // Annule le paiement en attente pour permettre un nouvel achat de quotas
  const handleAnnulerPaiementEnAttente = async () => {
    if (!paiementEnAttente) return;
    if (!window.confirm("Annuler cet achat de quota en attente ? Vous pourrez ensuite en relancer un nouveau.")) {
      return;
    }
    setAnnulationEnCours(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/abonnements/annuler_paiement.php`,
        { id_paiement: paiementEnAttente.id_paiement },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setPaiementEnAttente(null);
    } catch (err) {
      setServerError(err.response?.data?.message || "Impossible d'annuler cet achat.");
    } finally {
      setAnnulationEnCours(false);
    }
  };

  // Basculer la sélection d'un quota
  const toggleSelection = (typeValue) => {
    setAchats(achats.map(a => 
      a.type === typeValue ? { ...a, selected: !a.selected } : a
    ));
  };

  // Mettre à jour la quantité d'un quota
  const updateQuantite = (typeValue, quantite) => {
    setAchats(achats.map(a => 
      a.type === typeValue ? { ...a, quantite: parseInt(quantite) || 0 } : a
    ));
  };

  // Calculer le total des achats sélectionnés
  const calculerTotal = () => {
    return achats.reduce((total, achat) => {
      if (!achat.selected) return total;
      const type = TYPES_QUOTA.find(t => t.value === achat.type);
      return total + (type?.prix || 0) * (achat.quantite || 0);
    }, 0);
  };

  // Validation
  const validateForm = () => {
    const errors = [];

    if (!selectedSociete) {
      errors.push("Veuillez sélectionner une société");
    }

    let hasSelected = false;
    achats.forEach((achat) => {
      if (achat.selected) {
        hasSelected = true;
        if (achat.quantite < 1 || achat.quantite > 1000) {
          errors.push(`La quantité pour ${TYPES_QUOTA.find(t => t.value === achat.type)?.label} doit être entre 1 et 1000`);
        }
      }
    });

    if (!hasSelected) {
      errors.push("Veuillez sélectionner au moins un type de quota à acheter");
    }

    // Validation du numéro de reçu pour admin
    if (currentProfile === "1" && modePaiement === "especes" && !numeroRecu.trim()) {
      errors.push("Le numéro de reçu est requis pour les paiements en espèces");
    }

    return errors;
  };

  // Créer l'achat côté serveur (statut 'pending' pour un gestionnaire tant
  // que le paiement NITA/AMANA n'est pas confirmé) puis, si un paiement réel
  // est nécessaire, ouvrir la modal de paiement — remplace l'ancienne
  // "simulation de paiement" qui créditait le quota après un simple délai de
  // 3 secondes sans jamais rien faire payer.
  const finaliserAchat = async (data) => {
    setLoading(true);

    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE_URL}/achat_quota/create.php`;

      const response = await axios.post(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        if (response.data.statut_paiement === 'pending') {
          setPaiementEnCours({ id_paiement: response.data.id_paiement, montant: response.data.montant_total });
          setShowPaiementModal(true);
          setLoading(false);
          return;
        }

        let message = `✅ Achat effectué avec succès !\n\n`;
        response.data.achats.forEach(a => {
          message += `📦 ${a.quantite} ${a.type_label} : ${a.total.toLocaleString('fr-FR')} FCFA\n`;
        });
        message += `\n💰 Total: ${response.data.montant_total_formate}\n`;

        if (currentProfile === "1" && modePaiement === "especes") {
          message += `📝 Numéro de reçu: ${numeroRecu}\n`;
        }
        message += `\n🔄 Redirection vers la liste des achats...`;

        setSuccessMsg(message);

        setTimeout(() => {
          navigate("/quotas/list");
        }, 3000);
      } else {
        setServerError(response.data?.message || "Erreur lors de l'achat");
      }
    } catch (err) {
      console.error("Erreur:", err);

      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403 && err.response?.data?.code === 'PAIEMENT_EN_ATTENTE') {
        setPaiementEnAttente(err.response.data.paiement_en_attente || paiementEnAttente);
        setServerError(err.response.data.message);
      } else if (err.response?.status === 403 && err.response?.data?.code === 'ESSAI_ACHAT_INTERDIT') {
        setServerError(err.response.data.message);
      } else if (err.response?.status === 403) {
        setServerError("Permission refusée.");
      } else if (err.response?.status === 400) {
        if (err.response.data?.errors) {
          setValidationErrors(err.response.data.errors);
        } else {
          setServerError(err.response.data?.message || "Données invalides");
        }
      } else {
        setServerError(err.response?.data?.message || "Erreur technique. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Soumission
  // Soumission
const handleSubmit = async (e) => {
  e.preventDefault();

  const errors = validateForm();
  if (errors.length > 0) {
    setValidationErrors(errors);
    return;
  }

  // Préparer les achats sélectionnés
  const achatsValides = achats
    .filter(a => a.selected && a.quantite > 0)
    .map(a => ({
      type: a.type,
      quantite: a.quantite
    }));

  const dataToSend = {
    id_societe: selectedSociete.id_societe,
    achats: achatsValides,
    mode_paiement: modePaiement,
    numero_recu: currentProfile === "1" ? numeroRecu : null
  };

  if (currentProfile === "1") {
    // Admin : création directe
    setLoading(true);
    setServerError("");
    setValidationErrors([]);
    setSuccessMsg("");

    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE_URL}/achat_quota/create.php`;

      const response = await axios.post(url, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        let message = `✅ Achat effectué avec succès !\n\n`;
        response.data.achats.forEach(a => {
          message += `📦 ${a.quantite} ${a.type_label} : ${a.total.toLocaleString('fr-FR')} FCFA\n`;
        });
        message += `\n💰 Total: ${response.data.montant_total_formate}\n`;
        message += `📝 Numéro de reçu: ${numeroRecu}\n\n`;
        message += `🔄 Redirection vers la liste des achats...`;

        setSuccessMsg(message);

        setTimeout(() => {
          navigate("/quotas/list");
        }, 3000);
      } else {
        setServerError(response.data?.message || "Erreur lors de l'achat");
      }
    } catch (err) {
      console.error("Erreur:", err);

      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        // Vérifier si c'est une erreur d'abonnement
        if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
          setServerError(
            <div>
              <strong style={{ fontSize: '1.1rem' }}>⛔ Cette société n'a pas d'abonnement actif !</strong>
              <p className="mt-3 mb-2">
                Pour acheter des quotas, la société doit d'abord souscrire à un abonnement.
              </p>
              <p className="text-muted small mb-3">
                Les quotas sont liés à l'abonnement. Sans abonnement actif, impossible d'ajouter des quotas.
              </p>
              <div className="mt-2">
                <Link 
                  to="/abonnements/create" 
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: COLORS.primary,
                    color: COLORS.white,
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                  }}
                >
                  <i className="bi bi-plus-circle"></i>
                  Créer un abonnement pour cette société
                </Link>
              </div>
            </div>
          );
        } else if (err.response.data?.code === 'ESSAI_ACHAT_INTERDIT') {
          setServerError(err.response.data.message);
        } else if (err.response.data?.code === 'PAIEMENT_EN_ATTENTE') {
          setPaiementEnAttente(err.response.data.paiement_en_attente || paiementEnAttente);
          setServerError(err.response.data.message);
        } else {
          setServerError("Permission refusée.");
        }
      } else if (err.response?.status === 400) {
        if (err.response.data?.errors) {
          setValidationErrors(err.response.data.errors);
        } else {
          setServerError(err.response.data?.message || "Données invalides");
        }
      } else {
        setServerError(err.response?.data?.message || "Erreur technique. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  } else {
    // Gestionnaire : paiement réel via NITA/AMANA (voir finaliserAchat)
    finaliserAchat(dataToSend);
  }
};

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Achat de quotas</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/quotas/list">Achats</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
              Nouvel achat
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-8">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title mb-4" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.success
                    }}>
                      <i className="bi bi-cart-plus"></i>
                    </div>
                    Nouvel achat de quotas
                  </div>
                </h5>

                {/* Messages */}
                {serverError && (
                  <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{serverError}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setServerError("")}
                      ></button>
                    </div>
                  </div>
                )}

                {validationErrors.length > 0 && (
                  <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#fff3e0',
                    borderColor: COLORS.warning,
                    color: '#f57c00',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.warning}`
                  }}>
                    <div className="d-flex align-items-start">
                      <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                      <div className="flex-grow-1">
                        <h6 className="alert-heading mb-2" style={{ color: '#e65100' }}>
                          Erreurs de validation
                        </h6>
                        <ul className="mb-0 ps-3">
                          {validationErrors.map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                        </ul>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setValidationErrors([])}
                      ></button>
                    </div>
                  </div>
                )}

                {successMsg && (
                  <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`,
                    whiteSpace: "pre-line"
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2 fs-4"></i>
                      <div className="flex-grow-1">{successMsg}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setSuccessMsg("")}
                      ></button>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* Société */}
                  <div className="mb-4">
                    <label className="form-label fw-bold" style={{ color: COLORS.darkText }}>
                      <i className="bi bi-building me-2" style={{ color: COLORS.secondary }}></i>
                      Société <span className="text-danger">*</span>
                    </label>
                    
                    {currentProfile === "1" ? (
                      // Admin : select déroulant
                      <select
                        className="form-select"
                        value={selectedSociete?.id_societe || ""}
                        onChange={(e) => {
                          const societe = societes.find(s => s.id_societe === parseInt(e.target.value));
                          setSelectedSociete(societe);
                        }}
                        disabled={loading || loadingSocietes}
                        style={{
                          borderColor: '#e0e0e0',
                          borderRadius: '8px',
                          padding: '10px 12px'
                        }}
                      >
                        <option value="">Sélectionner une société</option>
                        {societes.map(s => (
                          <option key={s.id_societe} value={s.id_societe}>
                            {s.nom_societe}
                          </option>
                        ))}
                      </select>
                    ) : (
                      // Gestionnaire : affichage en lecture seule
                      <div className="p-3 rounded" style={{ 
                        backgroundColor: COLORS.lightBg, 
                        border: `1px solid ${COLORS.primary}` 
                      }}>
                        {loadingSocietes ? (
                          <span className="text-muted">Chargement de votre société...</span>
                        ) : selectedSociete ? (
                          <div className="d-flex align-items-center">
                            <i className="bi bi-building me-2" style={{ color: COLORS.primary }}></i>
                            <span className="fw-bold" style={{ color: COLORS.primary }}>
                              {selectedSociete.nom_societe}
                            </span>
                          </div>
                        ) : (
                          <span className="text-danger">Société non trouvée</span>
                        )}
                      </div>
                    )}
                    
                    {loadingSocietes && currentProfile === "1" && (
                      <small className="text-muted d-block mt-2">
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Chargement des sociétés...
                      </small>
                    )}
                  </div>

                  {/* Achat de quota myNITA/Amana en attente : bloque tout nouvel achat
                      tant qu'il n'est ni annulé ni (une fois expiré) régénéré — même
                      règle que pour les abonnements (AbonnementsCreate.js). */}
                  {paiementEnAttente && (
                    <div className="alert mb-4" style={{
                      backgroundColor: '#FFF3E0',
                      borderLeft: `4px solid ${COLORS.warning}`,
                      borderRadius: '8px'
                    }}>
                      <div className="d-flex">
                        <div className="me-3">
                          <i className="bi bi-hourglass-split fs-4" style={{ color: COLORS.warning }}></i>
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-2" style={{ color: COLORS.darkText }}>
                            {paiementEnAttente.expire ? 'Achat de quota expiré (24h)' : 'Achat de quota en attente'}
                          </h6>
                          <p className="mb-2" style={{ color: COLORS.darkText }}>
                            {new Intl.NumberFormat('fr-FR').format(paiementEnAttente.montant)} FCFA
                            <br />
                            {paiementEnAttente.gateway ? (
                              <span className="small">
                                Référence : <span style={{ fontFamily: 'monospace' }}>{paiementEnAttente.code_achat || paiementEnAttente.reference}</span>
                                {' '}({paiementEnAttente.gateway === 'nita' ? 'NITA' : 'Amana'})
                                {paiementEnAttente.expire
                                  ? ' — cette référence a expiré, générez-en une nouvelle.'
                                  : ' — valable 24h. Aucun nouvel achat de quota possible tant qu\'il est en attente.'}
                              </span>
                            ) : (
                              <span className="small">
                                Aucun moyen de paiement n'a encore été choisi pour cet achat. Aucun nouvel achat de quota n'est possible tant qu'il est en attente.
                              </span>
                            )}
                          </p>
                          {!paiementEnAttente.expire && paiementEnAttente.gateway && (
                            <p className="small mb-2" style={{ color: COLORS.darkText }}>
                              <i className="bi bi-clock-history me-1"></i>
                              <CompteARebours createdAt={paiementEnAttente.created_at} />
                            </p>
                          )}
                          <div className="d-flex gap-2">
                            {(paiementEnAttente.expire || !paiementEnAttente.gateway) && (
                              <button
                                type="button"
                                className="btn btn-sm fw-semibold"
                                onClick={handleFinaliserPaiementEnAttente}
                                style={{ backgroundColor: COLORS.primary, color: 'white', borderRadius: '6px' }}
                              >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                {paiementEnAttente.gateway ? 'Générer une nouvelle référence' : 'Choisir un moyen de paiement'}
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm fw-semibold"
                              onClick={handleAnnulerPaiementEnAttente}
                              disabled={annulationEnCours}
                              style={{ border: `1px solid ${COLORS.danger}`, color: COLORS.danger, borderRadius: '6px', backgroundColor: 'white' }}
                            >
                              <i className="bi bi-x-circle me-1"></i> {annulationEnCours ? 'Annulation...' : 'Annuler'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* L'achat de quotas n'est pas disponible pendant une période d'essai
                      (quota fixe, non extensible) — on bloque ici avant même de laisser
                      remplir le formulaire, plutôt que de laisser l'utilisateur se faire
                      refuser à la soumission (même règle que Dashboard.js /
                      AbonnementsCreate.js). */}
                  {checkingEssai ? (
                    <div className="text-center py-4">
                      <span className="spinner-border spinner-border-sm me-2" role="status" style={{ color: COLORS.primary }}></span>
                      <span className="text-muted">Vérification de l'abonnement...</span>
                    </div>
                  ) : societeEnEssai ? (
                    <div className="alert mb-0" style={{
                      backgroundColor: '#EDE7F6',
                      borderLeft: '4px solid #6D28D9',
                      borderRadius: '8px'
                    }}>
                      <div className="d-flex">
                        <div className="me-3">
                          <i className="bi bi-stars fs-4" style={{ color: '#6D28D9' }}></i>
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-2" style={{ color: COLORS.darkText }}>
                            Achat de quotas indisponible pendant l'essai
                          </h6>
                          <p className="mb-3" style={{ color: COLORS.darkText }}>
                            Cette société est en période d'essai gratuit (quota fixe, non extensible).
                            Souscrivez un abonnement payant pour pouvoir acheter des quotas supplémentaires.
                          </p>
                          <div className="d-flex gap-2">
                            <Link
                              to="/abonnements/create"
                              className="btn btn-sm fw-semibold"
                              style={{ backgroundColor: '#6D28D9', color: 'white', borderRadius: '6px', textDecoration: 'none' }}
                            >
                              <i className="bi bi-bag-check me-1"></i> Souscrire un abonnement
                            </Link>
                            <Link
                              to="/quotas/list"
                              className="btn btn-sm fw-semibold"
                              style={{ border: `1px solid ${COLORS.primary}`, color: COLORS.primary, borderRadius: '6px', backgroundColor: 'white', textDecoration: 'none' }}
                            >
                              Retour
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                  <>
                  {/* Cartes de quotas */}
                  <div className="mb-4">
                    <label className="form-label fw-bold mb-3" style={{ color: COLORS.darkText }}>
                      <i className="bi bi-tags me-2" style={{ color: COLORS.secondary }}></i>
                      Quotas à acheter <span className="text-danger">*</span>
                    </label>

                    <div className="row g-4">
                      {TYPES_QUOTA.map(type => {
                        const achat = achats.find(a => a.type === type.value);
                        const quota = quotasActuels?.[type.value] || { restant: 0 };
                        
                        return (
                          <div key={type.value} className="col-md-6">
                            <div 
                              className="card h-100"
                              style={{ 
                                cursor: 'pointer',
                                border: achat?.selected ? `2px solid ${type.color}` : '1px solid #e0e0e0',
                                transition: 'all 0.2s ease',
                                backgroundColor: achat?.selected ? type.color + '10' : 'white'
                              }}
                              onClick={() => toggleSelection(type.value)}
                            >
                              <div className="card-body">
                                <div className="d-flex align-items-start justify-content-between mb-3">
                                  <div className="d-flex align-items-center">
                                    <div 
                                      className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                      style={{
                                        width: '48px',
                                        height: '48px',
                                        backgroundColor: achat?.selected ? type.color : type.color + '20',
                                        color: achat?.selected ? 'white' : type.color
                                      }}
                                    >
                                      <i className={`bi ${type.icon} fs-4`}></i>
                                    </div>
                                    <div>
                                      <h6 className="mb-1" style={{ color: COLORS.darkText, fontWeight: '600' }}>
                                        {type.label}
                                      </h6>
                                      <span className="badge" style={{ 
                                        backgroundColor: type.color + '20',
                                        color: type.color,
                                        fontSize: '0.8rem',
                                        padding: '4px 8px',
                                        borderRadius: '4px'
                                      }}>
                                        {type.prix.toLocaleString('fr-FR')} FCFA / unité
                                      </span>
                                    </div>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={achat?.selected || false}
                                      onChange={() => {}}
                                      style={{
                                        width: '20px',
                                        height: '20px',
                                        cursor: 'pointer'
                                      }}
                                    />
                                  </div>
                                </div>
                                
                                {achat?.selected && (
                                  <div className="mt-3">
                                    <label className="form-label small text-muted">
                                      Quantité
                                    </label>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={achat.quantite}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        updateQuantite(type.value, e.target.value);
                                      }}
                                      min="1"
                                      max="1000"
                                      style={{
                                        borderColor: '#e0e0e0',
                                        borderRadius: '6px'
                                      }}
                                    />
                                    <div className="d-flex justify-content-between mt-2 small">
                                      <span className="text-muted">Total:</span>
                                      <span className="fw-bold" style={{ color: type.color }}>
                                        {(type.prix * achat.quantite).toLocaleString('fr-FR')} FCFA
                                      </span>
                                    </div>
                                    {quota.restant > 0 && (
                                      <div className="mt-2 small text-muted">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Actuellement: {quota.restant} restant(s)
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Mode de paiement */}
                  <div className="mb-4">
                    <label className="form-label fw-bold" style={{ color: COLORS.darkText }}>
                      <i className="bi bi-credit-card me-2" style={{ color: COLORS.secondary }}></i>
                      Mode de paiement <span className="text-danger">*</span>
                    </label>
                    
                    {currentProfile !== "1" && moyensActifs === null && (
                      <div className="text-muted small">
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Chargement des moyens de paiement...
                      </div>
                    )}

                    {currentProfile !== "1" && moyensActifs !== null && moyensActifs.length === 0 && (
                      <div className="alert alert-warning py-2 small mb-0">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Aucun moyen de paiement n'est actuellement disponible. Contactez l'administrateur.
                      </div>
                    )}

                    <div className="row g-3">
                      {getModesPaiement().map(mode => {
                        const logoSrc = mode.value === 'nita' ? '/assets/img/mynita_logo.png'
                          : mode.value === 'amana' ? '/assets/img/amana_logo.png'
                          : null;
                        const isSelected = modePaiement === mode.value;
                        return (
                          <div key={mode.value} className="col-md-6">
                            <div
                              className="card h-100"
                              style={{
                                cursor: 'pointer',
                                border: isSelected ? `2px solid ${COLORS.primary}` : '1px solid #e0e0e0',
                                backgroundColor: isSelected ? '#E3F2FD' : 'white',
                                borderRadius: '12px'
                              }}
                              onClick={() => setModePaiement(mode.value)}
                            >
                              <div className="card-body py-4 text-center">
                                {logoSrc ? (
                                  <img src={logoSrc} alt={mode.label} style={{ height: '56px', objectFit: 'contain', marginBottom: '0.75rem' }} />
                                ) : (
                                  <i className="bi bi-cash fs-1 mb-2 d-block" style={{ color: isSelected ? COLORS.primary : COLORS.darkText }}></i>
                                )}
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  <span className={`bi ${isSelected ? 'bi-record-circle' : 'bi-circle'}`}
                                    style={{ color: isSelected ? COLORS.primary : '#adb5bd' }}></span>
                                  <span className="fw-semibold" style={{ color: isSelected ? COLORS.primary : COLORS.darkText }}>
                                    {mode.label}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Numéro de reçu pour admin (espèces) */}
                    {currentProfile === "1" && modePaiement === "especes" && (
                      <div className="mt-4">
                        <label className="form-label fw-bold" style={{ color: COLORS.darkText }}>
                          <i className="bi bi-receipt me-2" style={{ color: COLORS.secondary }}></i>
                          Numéro de reçu <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={numeroRecu}
                          onChange={(e) => setNumeroRecu(e.target.value)}
                          placeholder="Ex: RECU-2024-001234"
                          disabled={loading}
                          style={{
                            borderColor: '#e0e0e0',
                            borderRadius: '8px',
                            padding: '10px 12px'
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Boutons */}
                  <div className="d-flex justify-content-end gap-3 mt-4 pt-4 border-top">
                    <Link
                      to="/quotas/list"
                      className="btn fw-semibold"
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#E3F2FD';
                        e.target.style.borderColor = COLORS.secondary;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-x-lg me-2"></i>
                      Annuler
                    </Link>
                    <button
                      type="submit"
                      className="btn fw-semibold"
                      disabled={loading || loadingSocietes || !!paiementEnAttente}
                      style={{
                        color: COLORS.white,
                        backgroundColor: (loading || paiementEnAttente) ? '#B0BEC5' : COLORS.success,
                        borderColor: (loading || paiementEnAttente) ? '#B0BEC5' : COLORS.success,
                        borderRadius: '8px',
                        padding: '10px 32px'
                      }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Traitement...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-cart-check me-2"></i>
                          {currentProfile === "1" ? 'Finaliser l\'achat' : 'Procéder au paiement'}
                        </>
                      )}
                    </button>
                  </div>
                  </>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* Colonne récapitulative */}
          <div className="col-lg-4">
            {/* Récapitulatif de l'achat */}
            <div className="card mb-4" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              backgroundColor: '#F5F7FA'
            }}>
              <div className="card-body">
                <h6 className="card-title mb-3" style={{ 
                  color: COLORS.primary,
                  fontWeight: '600',
                  fontSize: '1.1rem'
                }}>
                  <i className="bi bi-receipt me-2" style={{ color: COLORS.secondary }}></i>
                  Récapitulatif
                </h6>

                {selectedSociete && (
                  <div className="mb-3 p-2 rounded" style={{ backgroundColor: '#E3F2FD' }}>
                    <small className="text-muted d-block mb-1">Société</small>
                    <div className="fw-bold" style={{ color: COLORS.primary }}>
                      {selectedSociete.nom_societe}
                    </div>
                  </div>
                )}

                {achats.filter(a => a.selected && a.quantite > 0).map((achat) => {
                  const type = TYPES_QUOTA.find(t => t.value === achat.type);
                  return (
                    <div key={achat.type} className="d-flex justify-content-between mb-2">
                      <span className="text-muted">{type?.label}:</span>
                      <span className="fw-bold">
                        {achat.quantite} × {type?.prix.toLocaleString('fr-FR')} = {(type?.prix * achat.quantite).toLocaleString('fr-FR')} FCFA
                      </span>
                    </div>
                  );
                })}

                <div className="d-flex justify-content-between mt-3 pt-2 border-top">
                  <span className="fw-bold" style={{ fontSize: '1.1rem' }}>Total :</span>
                  <span className="fw-bold" style={{ fontSize: '1.2rem', color: COLORS.success }}>
                    {calculerTotal().toLocaleString('fr-FR')} FCFA
                  </span>
                </div>

                {currentProfile === "1" && modePaiement === "especes" && numeroRecu && (
                  <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#E3F2FD' }}>
                    <small className="text-muted d-block mb-1">Numéro de reçu</small>
                    <div className="fw-bold" style={{ color: COLORS.primary }}>
                      {numeroRecu}
                    </div>
                  </div>
                )}

                {currentProfile !== "1" && (
                  <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#E3F2FD' }}>
                    <small className="text-muted d-block mb-1">Paiement via</small>
                    <div className="fw-bold" style={{ color: COLORS.primary }}>
                      {modePaiement === 'nita' ? 'Nita' : 'Amana'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quotas actuels */}
            {quotasActuels && !loadingQuotas && (
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-3" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontSize: '1.1rem'
                  }}>
                    <i className="bi bi-pie-chart me-2" style={{ color: COLORS.secondary }}></i>
                    Quotas actuels
                  </h6>

                  {TYPES_QUOTA.map(type => {
                    const quota = quotasActuels?.[type.value] || { utilises: 0, total: 0, restant: 0 };
                    if (quota.total === 0) return null;
                    
                    return (
                      <div key={type.value} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="text-muted">{type.label}</small>
                          <small className="fw-bold">
                            {quota.utilises} / {quota.total}
                          </small>
                        </div>
                        <div className="progress" style={{ height: '8px' }}>
                          <div 
                            className="progress-bar" 
                            role="progressbar" 
                            style={{ 
                              width: `${(quota.utilises / quota.total) * 100}%`,
                              backgroundColor: type.color
                            }}
                          ></div>
                        </div>
                        <small className="text-muted d-block mt-1">
                          Restant : <span className="fw-bold">{quota.restant}</span>
                        </small>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {loadingQuotas && (
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body text-center py-4">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  <span className="text-muted">Chargement des quotas...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <PaiementNitaAmanaModal
        show={showPaiementModal}
        idPaiement={paiementEnCours?.id_paiement}
        montant={paiementEnCours?.montant}
        onClose={() => {
          setShowPaiementModal(false);
          setServerError("Paiement mis de côté : il reste en attente (valable 24h). Vous pouvez le finaliser ou l'annuler ci-dessus.");
          if (selectedSociete?.id_societe) loadQuotasSociete(selectedSociete.id_societe);
        }}
        onSuccess={() => {
          setShowPaiementModal(false);
          setSuccessMsg("✅ Paiement confirmé, quotas crédités !\n\n🔄 Redirection vers la liste des achats...");
          setTimeout(() => navigate("/quotas/list"), 3000);
        }}
      />

      {/* Styles CSS */}
      <style>{`
        .card {
          transition: all 0.2s ease-in-out;
        }
        .card[onClick]:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.12) !important;
        }
        .progress-bar {
          transition: width 0.3s ease;
        }
        .form-check-input:checked {
          background-color: ${COLORS.primary};
          border-color: ${COLORS.primary};
        }
        .modal.show {
          display: block;
          background-color: rgba(0,0,0,0.5);
        }
        @media (max-width: 768px) {
          .col-lg-8, .col-lg-4 {
            margin-bottom: 20px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default AchatQuota;